import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Save, Droplets, AlertCircle, Link2, CheckCircle2, Copy, ShieldCheck, Send, Download,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import StepIndex, { PrevNextBar } from "../components/StepIndex";
import { WATER_SHEET_SECTIONS, estimateLpd } from "../lib/waterSheetSchema";
import { OnboardingSectionEditor } from "../components/onboarding/OnboardingFieldEditor";

/**
 * Validate the required fields across the whole schema. Returns
 * an array of `{sectionKey, fieldKey, label}` for every missing
 * required value. Used on Submit to block + highlight.
 */
function validateRequired(data) {
  const missing = [];
  for (const s of WATER_SHEET_SECTIONS) {
    for (const f of s.fields) {
      if (!f.required) continue;
      const v = data[`${s.key}.${f.key}`];
      if (v === null || v === undefined || v === "") {
        missing.push({ sectionKey: s.key, fieldKey: f.key, label: f.label, sectionTitle: s.title });
        continue;
      }
      if (Array.isArray(v) && v.length === 0) {
        missing.push({ sectionKey: s.key, fieldKey: f.key, label: f.label, sectionTitle: s.title });
      }
    }
  }
  return missing;
}

/**
 * Iter 427.p34 — Water Purification Info Sheet — full-page form.
 *
 * Visually parallel to LeadOnboardingForm.jsx: ShieldCheck-style
 * header bar, left-nav StepIndex, section-by-section editor,
 * autosave-on-blur, PrevNext bar.
 *
 * Two mount modes controlled by `mode` prop:
 *   - "rep"     → authed rep editing/viewing a sheet
 *   - "public"  → customer submitting via a share token
 */
export default function WaterSheetForm({ mode = "rep" }) {
  const { id: routeId, token } = useParams();
  const navigate = useNavigate();

  const [sheet, setSheet] = useState(null);
  const [data, setData] = useState({});
  const [activeKey, setActiveKey] = useState(WATER_SHEET_SECTIONS[0].key);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  const [shareInfo, setShareInfo] = useState(null); // { token, expires_at, public_path }
  const [brand, setBrand] = useState({ name: "HidroVerse", logo_url: null, logo_url_light: null });
  const [submittedOk, setSubmittedOk] = useState(false); // public success screen
  const [missingRequired, setMissingRequired] = useState([]); // list of {sectionKey,fieldKey,label,sectionTitle}

  // ---- Load ----
  useEffect(() => {
    (async () => {
      try {
        if (mode === "public") {
          const { data: doc } = await api.get(`/public/water-info-sheet/${token}`);
          setSheet(doc);
          setData(doc.data || {});
          if (doc.brand && typeof doc.brand === "object") setBrand(doc.brand);
          if (doc.status === "submitted") setSubmittedOk(true);
        } else {
          const { data: doc } = await api.get(`/water-info-sheets/${routeId}`);
          setSheet(doc);
          setData(doc.data || {});
          if (doc.share_token && !doc.share_revoked) {
            setShareInfo({
              token: doc.share_token,
              expires_at: doc.share_expires_at,
              public_path: `/public/water-info-sheet/${doc.share_token}`,
            });
          }
        }
      } catch (e) {
        setErr(formatErr(e?.response?.data?.detail, "Could not load sheet"));
      }
    })();
  }, [mode, routeId, token]);

  const isLocked = sheet?.status === "reviewed";

  const setField = useCallback((sec, key, value) => {
    setData((prev) => ({ ...prev, [`${sec}.${key}`]: value }));
  }, []);

  const save = async ({ submit = false } = {}) => {
    if (busy) return;
    // Iter 427.p34.c — validate required fields on Submit only; a
    // silent Save (rep-side) is allowed to be incomplete.
    if (submit) {
      const missing = validateRequired(data);
      if (missing.length) {
        setMissingRequired(missing);
        setActiveKey(missing[0].sectionKey);
        toast.error(`Please fill in ${missing.length} required field${missing.length === 1 ? "" : "s"} before submitting.`);
        // Scroll the missing-fields card into view.
        setTimeout(() => {
          const el = document.querySelector('[data-testid="water-sheet-missing-required"]');
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        return;
      }
      setMissingRequired([]);
    }
    setBusy(true);
    try {
      if (mode === "public") {
        const businessName = data["site.business_name"] || "";
        await api.post(`/public/water-info-sheet/${token}`, {
          data,
          business_name: businessName,
          submit,
        });
      } else {
        const businessName = data["site.business_name"] || sheet?.business_name || "";
        await api.patch(`/water-info-sheets/${routeId}`, {
          data,
          business_name: businessName,
        });
      }
      setSavedAt(new Date().toISOString());
      if (submit) {
        setSubmittedOk(true);
        toast.success("Submission received. HidroVerse will be in touch shortly.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const downloadPublicPdf = async () => {
    try {
      const res = await api.get(`/public/water-info-sheet/${token}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const bn = (sheet?.business_name || "water-info-sheet").replace(/[^a-zA-Z0-9_-]/g, "-");
      a.download = `water-info-sheet-${bn}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed.");
    }
  };

  const shareLink = async () => {
    try {
      const { data: info } = await api.post(`/water-info-sheets/${routeId}/share-link`);
      setShareInfo(info);
      const url = `${window.location.origin}${info.public_path}`;
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied to clipboard.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create link.");
    }
  };

  const markReviewed = async () => {
    try {
      await api.post(`/water-info-sheets/${routeId}/mark-reviewed`);
      toast.success("Marked as reviewed. Sheet is now locked.");
      const { data: fresh } = await api.get(`/water-info-sheets/${routeId}`);
      setSheet(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't mark as reviewed.");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/water-info-sheets/${routeId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const bn = (sheet?.business_name || "water-info-sheet").replace(/[^a-zA-Z0-9_-]/g, "-");
      a.download = `water-info-sheet-${bn}-${routeId.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF download failed.");
    }
  };

  const activeSection = useMemo(
    () => WATER_SHEET_SECTIONS.find((s) => s.key === activeKey),
    [activeKey],
  );

  const completionPct = useMemo(() => {
    let filled = 0;
    let total = 0;
    for (const s of WATER_SHEET_SECTIONS) {
      for (const f of s.fields) {
        total += 1;
        const v = data[`${s.key}.${f.key}`];
        if (v === undefined || v === null || v === "") continue;
        if (Array.isArray(v) && v.length === 0) continue;
        filled += 1;
      }
    }
    return total ? Math.round((filled / total) * 100) : 0;
  }, [data]);

  const sectionComplete = useCallback(
    (sectionKey) => {
      const sec = WATER_SHEET_SECTIONS.find((s) => s.key === sectionKey);
      if (!sec || !sec.fields.length) return false;
      const filled = sec.fields.filter((f) => {
        const v = data[`${sectionKey}.${f.key}`];
        if (v === null || v === undefined || v === "") return false;
        if (Array.isArray(v) && v.length === 0) return false;
        return true;
      }).length;
      return filled / sec.fields.length >= 0.8;
    },
    [data],
  );

  const estimatedLpd = useMemo(() => {
    const bare = {};
    for (const key of Object.keys(data)) {
      if (key.startsWith("sizing.")) bare[key.slice("sizing.".length)] = data[key];
    }
    return estimateLpd(bare);
  }, [data]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="hv-card border-rose-500/40 bg-rose-500/5 p-4 flex items-center gap-2 text-sm text-rose-300">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      </div>
    );
  }
  if (!sheet) return <div className="p-8 text-sm text-[#647087]">Loading…</div>;

  // Iter 427.p34.c — Public submission success screen. Replace the
  // entire form with a big, clear confirmation so the customer knows
  // their data landed. No back-button, no re-edit — the form is
  // sealed on their side once submitted.
  if (mode === "public" && submittedOk) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="water-sheet-public-success">
        <div className="hv-card p-6 md:p-10 space-y-4 text-center border-emerald-400/40 bg-emerald-500/[0.06]">
          {brand.logo_url_light || brand.logo_url ? (
            <img
              src={brand.logo_url_light || brand.logo_url}
              alt={brand.name}
              className="h-10 mx-auto mb-2 object-contain"
            />
          ) : (
            <Droplets className="w-10 h-10 mx-auto text-[#1FB6C1]" />
          )}
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400" />
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-emerald-100">
            Thank you — your sheet is in!
          </h1>
          <p className="text-sm text-[#a0aabf] max-w-md mx-auto">
            {brand.name} has received your water purification information sheet
            and will reach out shortly. If we need anything else, we'll contact you
            on the details you provided.
          </p>
          <div className="pt-2">
            <button
              type="button"
              onClick={downloadPublicPdf}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="water-sheet-public-success-download"
            >
              <Download className="w-3.5 h-3.5" /> Download a copy (PDF)
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-16" data-testid={`water-sheet-form-${mode}`}>
      {/* ---- Top header ---- */}
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          {mode === "rep" && (
            <button
              type="button"
              onClick={() => navigate(`/tools/water-info-sheet`)}
              className="hv-btn hv-btn-ghost text-xs mb-2"
              data-testid="water-sheet-close"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sheets
            </button>
          )}
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight flex items-center gap-3 flex-wrap">
            {mode === "public" && (brand.logo_url_light || brand.logo_url) ? (
              <img
                src={brand.logo_url_light || brand.logo_url}
                alt={brand.name}
                className="h-8 md:h-10 object-contain"
              />
            ) : (
              <Droplets className="w-7 h-7 text-[#1FB6C1]" />
            )}
            Water Purification Info Sheet
            {mode === "public" && (
              <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300">
                Secure link · {brand.name}
              </span>
            )}
            {isLocked && (
              <span
                className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 inline-flex items-center gap-1"
                data-testid="water-sheet-locked-badge"
              >
                <ShieldCheck className="w-3 h-3" /> Reviewed · locked
              </span>
            )}
          </h1>
          <div className="text-xs text-[#a0aabf] mt-1 truncate">
            {sheet.business_name || "(new sheet)"}{sheet.submitted_at ? ` · submitted ${new Date(sheet.submitted_at).toLocaleDateString('en-GB')}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div
            className="text-[10px] uppercase tracking-wider text-[#647087] font-mono"
            data-testid="water-sheet-saved-status"
          >
            {savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString('en-GB')}` : sheet.updated_at ? `Last saved ${new Date(sheet.updated_at).toLocaleTimeString('en-GB')}` : "Not saved yet"}
          </div>
          {mode === "rep" && !isLocked && (
            <button
              type="button"
              onClick={shareLink}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="water-sheet-share-btn"
              title="Copy a public link a prospect can use to fill this in without logging in."
            >
              <Link2 className="w-3.5 h-3.5" /> {shareInfo ? "Copy link" : "Share link"}
            </button>
          )}
          {mode === "rep" && (
            <button
              type="button"
              onClick={downloadPdf}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="water-sheet-download-pdf"
              title="Download this sheet as a PDF. The customer's General Documents pane gets the same file when the sheet is submitted."
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
          )}
          {mode === "rep" && sheet.status === "submitted" && !isLocked && (
            <button
              type="button"
              onClick={markReviewed}
              className="hv-btn hv-btn-ghost text-xs text-emerald-300 hover:text-emerald-200"
              data-testid="water-sheet-mark-reviewed"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark reviewed
            </button>
          )}
          {!isLocked && mode === "rep" && (
            <button
              type="button"
              onClick={() => save()}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="water-sheet-save"
            >
              <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
            </button>
          )}
          {mode === "public" && (
            <button
              type="button"
              onClick={downloadPublicPdf}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="water-sheet-public-download-pdf"
              title="Prefer paper? Download the sheet, fill it in offline, and email it back."
            >
              <Download className="w-3.5 h-3.5" /> Download blank / partial PDF
            </button>
          )}
          {mode === "public" && !isLocked && (
            <button
              type="button"
              onClick={() => save({ submit: true })}
              disabled={busy}
              className="hv-btn hv-btn-primary text-xs bg-emerald-500 hover:bg-emerald-600 border-emerald-400"
              data-testid="water-sheet-public-submit"
            >
              <Send className="w-3.5 h-3.5" /> {busy ? "Submitting…" : "Submit"}
            </button>
          )}
        </div>
      </header>

      {/* Iter 427.p34.c — Public form: prominent data-loss warning
          replacing the old Save button. Sets clear expectations. */}
      {mode === "public" && !isLocked && (
        <div
          className="hv-card border-amber-400/40 bg-amber-500/[0.06] p-3 text-[12px] text-amber-100 flex items-start gap-2"
          data-testid="water-sheet-public-dataloss-banner"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-300" />
          <div>
            <span className="font-medium">Nothing is saved until you click Submit.</span>{" "}
            If you close this tab or leave the page, any information you've entered
            will be lost. If you'd prefer to fill this in on paper, download the PDF
            using the button in the top-right.
          </div>
        </div>
      )}

      {/* Missing required fields — list them + jump-to-section chip */}
      {missingRequired.length > 0 && (
        <div
          className="hv-card border-rose-500/40 bg-rose-500/[0.06] p-3 text-[12px] text-rose-100"
          data-testid="water-sheet-missing-required"
        >
          <div className="font-medium mb-1 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4 text-rose-300" />
            {missingRequired.length} required field{missingRequired.length === 1 ? " is" : "s are"} missing:
          </div>
          <ul className="ml-6 list-disc space-y-0.5">
            {missingRequired.map((m) => (
              <li key={`${m.sectionKey}.${m.fieldKey}`}>
                <button
                  type="button"
                  onClick={() => setActiveKey(m.sectionKey)}
                  className="underline hover:no-underline text-rose-200"
                  data-testid={`water-sheet-missing-jump-${m.sectionKey}-${m.fieldKey}`}
                >
                  {m.label}
                </button>{" "}
                <span className="text-rose-300/70">— in {m.sectionTitle}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Share-link banner (rep view only) */}
      {mode === "rep" && shareInfo && !isLocked && (
        <div
          className="hv-card border-cyan-400/30 bg-cyan-500/[0.03] p-3 flex items-center gap-2 text-xs"
          data-testid="water-sheet-share-banner"
        >
          <Link2 className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
          <span className="text-[#a0aabf] truncate flex-1">
            Public link active until {new Date(shareInfo.expires_at).toLocaleDateString('en-GB')}
          </span>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}${shareInfo.public_path}`);
              toast.success("Copied.");
            }}
            className="hv-btn hv-btn-ghost text-[11px]"
            data-testid="water-sheet-share-copy"
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      )}

      {/* Sizing peek — surfaces live estimate as headcounts get filled */}
      {estimatedLpd > 0 && (
        <div
          className="hv-card border-amber-400/30 bg-amber-500/[0.03] p-3 text-xs text-amber-100"
          data-testid="water-sheet-sizing-preview"
        >
          Based on your head-count block, we estimate{" "}
          <span className="font-mono font-bold">{estimatedLpd.toLocaleString()} L/day</span>{" "}
          demand for this facility type. You can override this on the "Litres per day" field.
        </div>
      )}

      {/* ---- Body: section nav (left) + form (right) ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <div className="self-start">
          <StepIndex
            sections={WATER_SHEET_SECTIONS.map((s) => ({ key: s.key, title: s.title }))}
            activeKey={activeKey}
            onJump={setActiveKey}
            completionPct={completionPct}
            isComplete={sectionComplete}
          />
        </div>
        <div>
          <OnboardingSectionEditor
            section={activeSection}
            data={data}
            setField={setField}
            isLocked={isLocked}
            testIdPrefix="water-sheet"
          />
          <PrevNextBar
            sections={WATER_SHEET_SECTIONS.map((s) => ({ key: s.key, title: s.title }))}
            activeKey={activeKey}
            onJump={setActiveKey}
            testId="water-sheet-step-nav"
          />
          {/* Public form CTA at the bottom of the last section */}
          {mode === "public" && !isLocked && activeKey === WATER_SHEET_SECTIONS[WATER_SHEET_SECTIONS.length - 1].key && (
            <div className="hv-card p-4 mt-4 flex items-center justify-between gap-3">
              <div className="text-xs text-[#a0aabf]">
                Ready? Submit will lock the sheet and notify HidroVerse.
              </div>
              <button
                type="button"
                onClick={() => save({ submit: true })}
                disabled={busy}
                className="hv-btn hv-btn-primary text-xs bg-emerald-500 hover:bg-emerald-600 border-emerald-400"
                data-testid="water-sheet-public-submit-footer"
              >
                <Send className="w-3.5 h-3.5" /> Submit sheet
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
